/**
 * Login Page Component
 *
 * Professional login interface for Azure AD Single Sign-On.
 * Design inspired by LabBridge with medical aesthetic.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Container, Spinner } from 'react-bootstrap';
import {
  FiArrowRight,
  FiShield,
  FiActivity,
  FiClock,
  FiCheckCircle,
  FiUsers,
  FiBarChart2,
  FiBell,
} from 'react-icons/fi';
import { useAuth } from '../auth/AuthContext';

const LoginPage: React.FC = () => {
  const { isAuthenticated, isLoading, error, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loginError, setLoginError] = useState<string | null>(null);
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  // Redirect to previous page or dashboard if already authenticated
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate, location]);

  const handleLogin = async () => {
    try {
      setIsLoggingIn(true);
      setLoginError(null);
      await login();
    } catch (err) {
      if (err instanceof Error) {
        if (err.message.includes('user_cancelled')) {
          setLoginError('Inicio de sesión cancelado.');
        } else if (err.message.includes('interaction_in_progress')) {
          setLoginError('Sesión anterior incompleta. Por favor recarga la página.');
          sessionStorage.clear();
        } else {
          setLoginError(err.message);
        }
      } else {
        setLoginError('Error desconocido al iniciar sesión.');
      }
    } finally {
      setIsLoggingIn(false);
    }
  };

  if (isLoading) {
    return (
      <div className="login-page">
        <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
          <Spinner animation="border" variant="light" />
        </Container>
      </div>
    );
  }

  return (
    <div className="login-page">
      {/* Background gradient */}
      <div className="login-background" />

      {/* Header */}
      <header className="login-header">
        <div className="login-logo">
          <img src="/logo-brain.svg" alt="MedInterpret" className="login-logo-icon" />
          <div className="login-logo-text">
            <span className="login-logo-name">MedInterpret</span>
            <span className="login-logo-by">by Previta</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="login-main">
        <div className="login-content">
          {/* Left Column - Hero */}
          <div className="login-hero">
            <div className="login-status-badge">
              <span className="status-dot" />
              Sistema activo y operacional
            </div>

            <h1 className="login-title">
              Auditoría inteligente de{' '}
              <span className="text-gradient">interpretaciones médicas</span>
            </h1>

            <p className="login-subtitle">
              Revisa, edita y aprueba interpretaciones de laboratorio generadas por IA
              con firma digital y trazabilidad completa.
            </p>

            {/* Stats */}
            <div className="login-stats">
              <div className="login-stat">
                <span className="login-stat-value">99.9%</span>
                <span className="login-stat-label">Precisión IA</span>
              </div>
              <div className="login-stat">
                <span className="login-stat-value">&lt;2min</span>
                <span className="login-stat-label">Tiempo de revisión</span>
              </div>
              <div className="login-stat">
                <span className="login-stat-value">100%</span>
                <span className="login-stat-label">Trazabilidad</span>
              </div>
            </div>

            {/* Login Button */}
            <button
              className="login-button"
              onClick={handleLogin}
              disabled={isLoggingIn}
            >
              {isLoggingIn ? (
                <>
                  <Spinner animation="border" size="sm" />
                  <span>Iniciando sesión...</span>
                </>
              ) : (
                <>
                  <img
                    src="https://docs.microsoft.com/en-us/azure/active-directory/develop/media/howto-add-branding-in-azure-ad-apps/ms-symbollockup_mssymbol_19.svg"
                    alt="Microsoft"
                    className="ms-logo"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                  <span>Iniciar sesión con Microsoft</span>
                  <FiArrowRight className="arrow-icon" />
                </>
              )}
            </button>

            {/* Error Message */}
            {(error || loginError) && (
              <div className="login-error">
                {error?.message || loginError}
              </div>
            )}

            <p className="login-access-note">
              Acceso exclusivo para personal autorizado de Previta
            </p>
          </div>

          {/* Right Column - Features Card */}
          <div className="login-features-card">
            <h3 className="features-title">Todo lo que necesitas</h3>

            <div className="feature-item">
              <div className="feature-icon">
                <FiActivity />
              </div>
              <div className="feature-content">
                <h4>Interpretaciones IA</h4>
                <p>Análisis automatizado de resultados con modelos de última generación</p>
              </div>
            </div>

            <div className="feature-item">
              <div className="feature-icon">
                <FiClock />
              </div>
              <div className="feature-content">
                <h4>Control de SLA</h4>
                <p>Monitoreo en tiempo real de tiempos de respuesta por empresa</p>
              </div>
            </div>

            <div className="feature-item">
              <div className="feature-icon">
                <FiShield />
              </div>
              <div className="feature-content">
                <h4>Firma Digital</h4>
                <p>Autenticación Azure AD con foto y nombre del auditor</p>
              </div>
            </div>

            <div className="features-checklist">
              <div className="checklist-item">
                <FiCheckCircle />
                <span>Panel de auditoría centralizado</span>
              </div>
              <div className="checklist-item">
                <FiCheckCircle />
                <span>Historial completo de revisiones</span>
              </div>
              <div className="checklist-item">
                <FiCheckCircle />
                <span>Notificaciones automáticas</span>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="login-footer">
        <div className="footer-left">
          &copy; 2026 Previta. Todos los derechos reservados.
        </div>
        <div className="footer-right">
          Versión 1.0 &bull; Integración Azure AD
        </div>
      </footer>

      <style>{`
        .login-page {
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          position: relative;
          overflow-x: hidden;
        }

        .login-background {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, #0a1628 0%, #0d2847 50%, #0f3460 100%);
          z-index: -2;
        }

        .login-background::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background:
            radial-gradient(ellipse at 20% 50%, rgba(16, 185, 129, 0.08) 0%, transparent 50%),
            radial-gradient(ellipse at 80% 20%, rgba(0, 119, 230, 0.1) 0%, transparent 40%),
            radial-gradient(ellipse at 60% 80%, rgba(16, 185, 129, 0.05) 0%, transparent 40%);
          z-index: -1;
        }

        /* Header */
        .login-header {
          padding: 1.5rem 2rem;
          display: flex;
          align-items: center;
        }

        .login-logo {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .login-logo-icon {
          width: 40px;
          height: 40px;
        }

        .login-logo-text {
          display: flex;
          flex-direction: column;
        }

        .login-logo-name {
          font-size: 1.25rem;
          font-weight: 700;
          color: #fff;
          letter-spacing: -0.02em;
        }

        .login-logo-by {
          font-size: 0.75rem;
          color: rgba(255, 255, 255, 0.5);
        }

        /* Main Content */
        .login-main {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 2rem;
        }

        .login-content {
          display: grid;
          grid-template-columns: 1fr 400px;
          gap: 4rem;
          max-width: 1200px;
          width: 100%;
        }

        /* Hero Section */
        .login-hero {
          display: flex;
          flex-direction: column;
          justify-content: center;
        }

        .login-status-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 100px;
          padding: 0.5rem 1rem;
          font-size: 0.875rem;
          color: rgba(255, 255, 255, 0.8);
          width: fit-content;
          margin-bottom: 1.5rem;
        }

        .status-dot {
          width: 8px;
          height: 8px;
          background: #10B981;
          border-radius: 50%;
          box-shadow: 0 0 8px #10B981;
          animation: pulse 2s infinite;
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }

        .login-title {
          font-size: 3.5rem;
          font-weight: 700;
          color: #fff;
          line-height: 1.1;
          margin-bottom: 1.5rem;
          letter-spacing: -0.03em;
        }

        .text-gradient {
          background: linear-gradient(135deg, #10B981 0%, #34D399 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .login-subtitle {
          font-size: 1.125rem;
          color: rgba(255, 255, 255, 0.7);
          line-height: 1.6;
          margin-bottom: 2rem;
          max-width: 540px;
        }

        /* Stats */
        .login-stats {
          display: flex;
          gap: 3rem;
          margin-bottom: 2.5rem;
        }

        .login-stat {
          display: flex;
          flex-direction: column;
        }

        .login-stat-value {
          font-size: 2rem;
          font-weight: 700;
          color: #fff;
          letter-spacing: -0.02em;
        }

        .login-stat-label {
          font-size: 0.875rem;
          color: rgba(255, 255, 255, 0.5);
        }

        /* Login Button */
        .login-button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.75rem;
          background: rgba(255, 255, 255, 0.95);
          border: none;
          border-radius: 12px;
          padding: 1rem 2rem;
          font-size: 1rem;
          font-weight: 600;
          color: #1a1a1a;
          cursor: pointer;
          transition: all 0.2s ease;
          width: fit-content;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
        }

        .login-button:hover:not(:disabled) {
          background: #fff;
          transform: translateY(-2px);
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.4);
        }

        .login-button:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .login-button .ms-logo {
          width: 20px;
          height: 20px;
        }

        .login-button .arrow-icon {
          transition: transform 0.2s ease;
        }

        .login-button:hover .arrow-icon {
          transform: translateX(4px);
        }

        .login-error {
          margin-top: 1rem;
          padding: 0.75rem 1rem;
          background: rgba(239, 68, 68, 0.2);
          border: 1px solid rgba(239, 68, 68, 0.3);
          border-radius: 8px;
          color: #FCA5A5;
          font-size: 0.875rem;
          max-width: 400px;
        }

        .login-access-note {
          margin-top: 1.5rem;
          font-size: 0.875rem;
          color: rgba(255, 255, 255, 0.5);
        }

        /* Features Card */
        .login-features-card {
          background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 24px;
          padding: 2rem;
        }

        .features-title {
          font-size: 1.25rem;
          font-weight: 600;
          color: #fff;
          margin-bottom: 1.5rem;
        }

        .feature-item {
          display: flex;
          gap: 1rem;
          margin-bottom: 1.5rem;
        }

        .feature-icon {
          width: 44px;
          height: 44px;
          background: linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(16, 185, 129, 0.1) 100%);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #10B981;
          font-size: 1.25rem;
          flex-shrink: 0;
        }

        .feature-content h4 {
          font-size: 1rem;
          font-weight: 600;
          color: #fff;
          margin-bottom: 0.25rem;
        }

        .feature-content p {
          font-size: 0.875rem;
          color: rgba(255, 255, 255, 0.6);
          margin: 0;
          line-height: 1.4;
        }

        .features-checklist {
          margin-top: 1.5rem;
          padding-top: 1.5rem;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
        }

        .checklist-item {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          color: rgba(255, 255, 255, 0.7);
          font-size: 0.875rem;
          margin-bottom: 0.75rem;
        }

        .checklist-item svg {
          color: #10B981;
          flex-shrink: 0;
        }

        /* Footer */
        .login-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1.5rem 2rem;
          color: rgba(255, 255, 255, 0.4);
          font-size: 0.875rem;
        }

        /* Responsive */
        @media (max-width: 1024px) {
          .login-content {
            grid-template-columns: 1fr;
            gap: 2rem;
          }

          .login-title {
            font-size: 2.5rem;
          }

          .login-features-card {
            max-width: 500px;
          }
        }

        @media (max-width: 640px) {
          .login-title {
            font-size: 2rem;
          }

          .login-stats {
            flex-wrap: wrap;
            gap: 1.5rem;
          }

          .login-footer {
            flex-direction: column;
            gap: 0.5rem;
            text-align: center;
          }
        }
      `}</style>
    </div>
  );
};

export default LoginPage;
